/*
Access Control Mixins:
* canLogin
* userName
* userId
* loginTime
* canAccess
* isSuper
* isManager
* userRole
--------------------------------------------------
*/

import { IS_DEV } from '@/components/Data'
import * as authLib from '@/lib/auth'
import * as sessionLib from '@/lib/session'

export default {

  methods: {

    xolasClient () {
      return this.getStore().xolasClient
    },

    profile () {
      return this.getStore().profile
    },

    org () {
      return this.getStore().org
    },

    // XOL-526 (UX U-J): the Tower Co's display name, for copy that names it
    // to a counterparty ("Only {Tower Co} can remove this file."). A method,
    // not a data() snapshot — xolasClient() is populated by an async
    // bootstrap XHR and must re-evaluate as the store hydrates.
    towerCoName () {
      var c = this.xolasClient()
      return (c && c.name) ? c.name : 'Xolas TMS'
    },

    showLoginStatus () {
      console.log('UserName:', this.userName(), 'Logged In: ', this.loginTime())
    },



    canLogin (user, psw) {

      if (user) {
        user = user.trim()
      } else {
        this.$message({ message: 'Invalid user or email', type: 'warning', center: true })
        return
      }

      if (psw) {
        psw = psw.trim()
      } else {
        this.$message({ message: 'Invalid Password', type: 'warning', center: true })
        return
      }

      // KAN-39: send plaintext password over TLS — API bcrypts server-side.
      // The session JWT + refresh tokens are stored in lib/auth.js.
      // XOL-370: mark the login in-flight so a racing bootstrap 401 during
      // the post-login chain below doesn't trip axiosClient's forced-logout
      // path and bounce the user back to /login with a phantom
      // session-expired flash.
      authLib.setLoginInFlight(true)
      return authLib.login({ login: user, password: psw })
        .then((result) => {
          const user = result && result.user ? result.user : null
          if (user) {
            this.getStore().profile = user
            if (user.organisation) {
              this.getStore().org = user.organisation
            }
            this.getStore().profile.loginTime = new Date()
            this.getStore().profile.allowedHost = this.getStore().hostname
          }
          this.saveStore()

          if (this.org() && this.org().id) {
            this.refreshStats(this.org().id)
          }

          // XOL-357: table metadata (config.meta) is only populated by
          // updateConfig(). On a fresh login the app booted unauthenticated, so
          // meta is still empty. Reload it (authenticated) BEFORE navigating so
          // deep-linked views that read getTableStruct(...) in data() render
          // their add-dialogs. updateConfig() always resolves, so a failed
          // config fetch still lets login complete and navigate.
          //
          // XOL-370: do NOT flip isLoggedIn before navigating. App.vue swaps
          // <login> -> <home> on isLoggedIn alone; setting it while the route is
          // still /login makes <home>'s <router-view> render the /login route
          // (the Login component) inside the authenticated shell — a second
          // login page — for the whole updateConfig() window, and the dashboard
          // route never shows its own loading. Navigate first; the router guard
          // (main.js beforeEach) flips isLoggedIn=true atomically as it resolves
          // the authenticated route, so the shell and the target route swap in
          // together. finalize() persists the store, re-asserts the flag (covers
          // a duplicate-navigation no-op) and clears the in-flight guard once
          // navigation settles.
          // XOL-446: fetch the full (JWT-gated) client org explicitly rather
          // than relying on the deleted `reloadClient` flag, which was never
          // re-armed after a logout and so left a second login in the same
          // page load looking at the first user's client. Both calls always
          // resolve, so neither can block login.
          return Promise.all([this.updateConfig(), this.fetchFullClient()]).then(() => {
            // The next genuine forced logout is allowed to reload again, and
            // starts from a clean logout-reason latch.
            sessionLib.clearTeardownFuse()
            sessionLib.clearLogoutReason()
            var target = authLib.takePostLoginRedirect()
            var finalize = () => {
              this.getStore().isLoggedIn = true
              this.saveStore()
              authLib.setLoginInFlight(false)
            }
            this.$router.push(target || '/', finalize, finalize)
          })
        })
        .catch((error) => {
          console.log('login error', error)
          this.getStore().isLoggedIn = false
          authLib.setLoginInFlight(false)
          const message = (error && error.response && error.response.data && error.response.data.error)
            ? error.response.data.error
            : (error && error.message ? error.message : 'Login failed')
          this.$message({ message: message, type: 'warning', center: true })
        })
    },


    logout () {
      // XOL-446: full teardown, not the old five-key resetStore() + saveStore().
      //
      // endSession() POSTs /api/logout (so the API revokes the refresh token
      // and clears the HttpOnly xolas_auth cookie), runs every registered
      // in-memory reset, wipes both storages except the post-login redirect
      // and the logout reason, expires JS-readable cookies, and then reloads.
      //
      // The saveStore() that used to sit here is deliberately gone: it is the
      // only post-boot writer of localStorage['xolas-user'], so calling it
      // after the wipe would put the outgoing user's state straight back.
      //
      // The router push is the fallback for when the reload is suppressed by
      // the loop fuse — after a real reload the guard lands us on /login
      // anyway.
      return sessionLib.endSession({ hard: true, callApi: true }).then(() => {
        var current = this.$router && this.$router.currentRoute ? this.$router.currentRoute.path : null
        if (current !== '/login') {
          this.$router.push('/login')
        }
      })
    },



    userName () {
      return (this.profile().name ? this.profile().name : this.profile().login)
    },


    loggedIn () {

      if (this.getStore().isLoggedIn) {
        return true
      } else {
        return false
      }

    },


    userId () {
      return this.profile().login
    },



    loginTime () {
      this.getStore().loginTime = new Date()
      return this.getStore().loginTime
    },


    canChange () {
      return !this.isViewOnly()
    },


    access (bool, feature = null) {
      return bool && (feature ? this.canAccess(feature) : true)
    },



    // --- is Orgs --------------------------------------------------------

    isAuthority (feature = null) {
      return this.access(this.profile().orgCategory === 'Authority', feature)
    },



    isBackhaulCo (feature = null) {
      return this.access(this.profile().orgCategory === 'Backhaul', feature)
    },



    isContractorCo (feature = null) {
      return this.access(this.profile().orgCategory === 'Contractor', feature)
    },



    isLandlordCo (feature = null) {
      return this.access(this.profile().orgCategory === 'Landlord', feature)
    },



    isInsuranceCo (feature = null) {
      return this.access(this.profile().orgCategory === 'Insurance', feature)
    },



    isTelco (feature = null) {
      return this.access(this.profile().orgCategory === 'Telco', feature)
    },



    isTowerCo (feature = null) {
      return this.access(this.profile().orgCategory === 'Tower Company', feature)
    },



    isTowerSupplierCo (feature = null) {
      return this.access(this.profile().orgCategory === 'Tower Supplier', feature)
    },



    isUtility (feature = null) {
      return this.access(this.profile().orgCategory === 'Utility', feature)
    },



    isApprover (feature = null) {
      if (this.isSuper()) {
        return true
      } else {
        return this.access(this.profile().approver, feature)
      }
    },


    // -- isXX Profile roles ----------------------------------------------

    isManager (feature = null) {
      if (this.isSuper()) {
        return true
      } else {
        return this.access(this.profile().manager, feature)
      }
    },


    isSuper () {
      if (!this.isTowerCo()) {
        return false
      } else {
        return (this.profile().super || this.profile().userRole === 'super')
      }
    },


    /*
      This is group admin view- not super user admin.
     */
    isAdminView () {
      return this.profile().defaultView === 'admin' || !this.profile().defaultView || this.isManagementView()
    },


    isBackhaulView () {
      return this.profile().defaultView === 'backhaul'
    },


    isContractorView () {
      return this.profile().defaultView === 'contractor'
    },


    isLandlordView () {
      return this.profile().defaultView === 'landlord'
    },


    isFinanceView () {
      return this.profile().defaultView === 'finance'
    },


    isTowerSupplierView () {
      return this.profile().defaultView === 'tower-supplier'
    },


    isManagementView () {
      return this.profile().defaultView === 'management' || this.isManager()
    },


    isMaintView () {
      return this.profile().defaultView === 'maintenance'
    },


    isProjectView () {
      return this.profile().defaultView === 'project'
    },


    isPropertyView () {
      return this.profile().defaultView === 'property'
    },


    isTelcoView () {
      return this.profile().defaultView === 'telco'
    },


    isViewOnly () {
      return (this.profile().viewer ? this.profile().viewer : false)
    },



    userRoles () {
      return this.profile().userRole
    },



    canAccessHost (host) {
      if (host) {

        if (IS_DEV) {
          return true
        } else {
          return host.split('|').includes(location.hostname)
        }

      } else {

        if (this.isSuper()) {
          return true
        } else {
          return false
        }

      }
    },



    canAccess (group) {

      if (this.isSuper()) {

        return true

      } else {

        if (group) {
          // TODO need to check for multiple roles in userRole
          var role = this.profile().userRole

          if (!role) {
            role = this.profile().orgCategory.toLowerCase()
          }

          if (role.includes('|')) {

            var list = role.split('|')
            var acl = new Array(list.length)


            for (var i = 0; i < list.length; i++) {
              acl[i] = group.split('|').includes(list[i])
            }

            for (i = 0; i < acl.length; i++) {
              if (acl[i]) {
                return true
              }
            }

          } else {
            return group.split('|').includes(role)
          }
        } else {
          return false
        }
      }
    }

  }
}
